import { BlurView } from 'expo-blur';
import React from 'react';
import { Pressable, Text, TextInput, View } from 'react-native';
import { AuthScreenLayout } from './components/auth-screen-layout';
import { useOtpVerification } from './hooks/use-otp-verification';

export function VerifyOTPScreen() {
  const otp = useOtpVerification();
  const { boxWidth, boxHeight, fontSize, gap } = otp.computeBoxSizes();

  return (
    <AuthScreenLayout title="Verify Email" paddingHorizontal={32}>
      <View className="items-center mb-8" style={{ marginTop: -32 }}>
        <Text className="text-2xl font-bold mt-4 text-warm-gray-100">
          Verify Your Email
        </Text>
        <Text className="text-base mt-2 text-center text-warm-gray-100">
          We&apos;ve sent a 6-digit verification code to
        </Text>
        <Text className="text-base font-semibold mb-2 text-warm-gray-100">
          {otp.userEmail}
        </Text>
        <Text className="text-sm text-center text-warm-gray-300">
          Please check your email and enter the code below
        </Text>
      </View>

      <BlurView intensity={50} className="rounded-xl overflow-hidden p-6" style={{ width: '100%' }}>
        <View className="space-y-6">
          <View style={{ width: '100%' }} onLayout={otp.handleOtpRowLayout}>
            <View className="flex-row justify-center">
              {otp.otp.map((digit, index) => (
                <TextInput
                  key={index}
                  ref={(ref) => { otp.inputRefs.current[index] = ref; }}
                  className={`text-center font-bold rounded-lg border bg-black/50 text-warm-gray-100 ${
                    otp.error ? 'border-red-500' : 'border-white/50'
                  }`}
                  style={{
                    width: boxWidth,
                    height: boxHeight,
                    fontSize,
                    marginRight: index < 5 ? gap : 0,
                  }}
                  value={digit}
                  onChangeText={(value) => otp.handleOTPChange(value, index)}
                  onKeyPress={(event) => otp.handleKeyPress(event, index)}
                  keyboardType="numeric"
                  textContentType={index === 0 ? 'oneTimeCode' : 'none'}
                  autoComplete={index === 0 ? 'sms-otp' : 'off'}
                  importantForAutofill={index === 0 ? 'yes' : 'no'}
                  selectTextOnFocus
                  editable={!otp.isLoading}
                  accessibilityLabel={`Verification code digit ${index + 1} of 6`}
                />
              ))}
            </View>
          </View>

          {otp.error && (
            <Text className="text-red-500 text-sm text-center mt-2" accessibilityLiveRegion="polite">
              {otp.error}
            </Text>
          )}

          <Pressable
            className={`p-4 rounded-lg bg-primary ${otp.isLoading ? 'opacity-50' : ''}`}
            onPress={() => otp.handleVerifyOTP()}
            disabled={otp.isLoading || otp.otp.some(d => d === '')}
          >
            <Text className="font-semibold text-center text-warm-gray-950">
              {otp.isLoading ? 'Verifying...' : 'Verify Code'}
            </Text>
          </Pressable>

          <View className="flex-row justify-center items-center space-x-2">
            <Text className="text-warm-gray-100">Didn&apos;t receive the code?</Text>
            <Pressable
              onPress={otp.handleResendOTP}
              disabled={otp.resendCooldown > 0 || otp.isResending}
              className={otp.resendCooldown > 0 || otp.isResending ? 'opacity-50' : ''}
            >
              <Text className="font-semibold underline text-warm-gray-100">
                {otp.resendCooldown > 0
                  ? `Resend in ${otp.resendCooldown}s`
                  : otp.isResending
                    ? 'Sending...'
                    : 'Resend Code'}
              </Text>
            </Pressable>
          </View>
        </View>
      </BlurView>

      <View className="flex-row justify-center mt-6">
        <Pressable onPress={otp.navigateToLogin}>
          <Text className="font-semibold underline text-warm-gray-100">
            Back to Login
          </Text>
        </Pressable>
      </View>
    </AuthScreenLayout>
  );
}
